import { Grid, Card, Typography, Box, useMediaQuery, Divider } from "@mui/material"
import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Markdown from "../../../reusable-components/markdown";
import { useTabs } from "../../../context/TabContext";
import Iconify from '../../../reusable-components/iconify';
import { cloudinaryThumbnail } from '../../../utils/formatCloudinary';
import Image from '../../../reusable-components/image';
import Scrollbar from '../../../reusable-components/scrollbar';
import { fMonthDayYearShort } from '../../../utils/formatTime';
import { highlightText } from '../../../reusable-components/highlight-element/highlight-element';

OrderResult.propTypes = {
  searchResult: PropTypes.object,
  searchValue: PropTypes.string,
  isDialog: PropTypes.bool,
  setIsDialogOpen: PropTypes.func
}

export default function OrderResult ({ searchResult, searchValue, isDialog = false, setIsDialogOpen }) {
  const { addTab } = useTabs();
  const navigate = useNavigate();
  const mediumAndUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const [open, setOpen] = useState(searchResult?.items?.length > 0);
  
  const handleClick = () => {
    setOpen(!open);
  };

  const openOrderInNewTab = () => {
    addTab();
    navigate(`/dashboard/orders/${searchResult.orderId}`);
  };

  const headerItemStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderRadius: '5px',
    padding: '3px 5px',
  }

  return <>
    <Grid
      container
      flexWrap="nowrap"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <Grid item>
        <Box>
          <Typography variant="subtitle1" sx={{fontWeight: 'bold', display: 'flex'}}>
            Order #
            <Box onClick={() => openOrderInNewTab()} sx={{ cursor: 'pointer', display: 'inline-block' }}>
              <Markdown
                sx={{fontWeight: 'bold', textDecoration: 'underline'}}
                inline
                convertPToDiv
                children={`${highlightText(searchResult.orderNumber, searchValue)}`}
              />
            </Box>
            &nbsp; | &nbsp;
            <Markdown sx={{fontWeight: 'bold'}} inline convertPToDiv children={`${highlightText(searchResult.department, searchValue)}`} />
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {searchResult.facility && (
            <Box sx={headerItemStyle}>
              <Typography variant="subtitle2">
                <Markdown convertPToDiv children={`<s2>Facility: ${highlightText(searchResult.facility, searchValue)}</s2>`} />
              </Typography>
            </Box>
          )}
          {searchResult.facilityManagers?.length > 0 && (
            <Box sx={headerItemStyle}>
              <Typography variant="subtitle2">
                <Markdown
                  convertPToDiv
                  children={`<s2>Managers: ${highlightText(searchResult.facilityManagers.map(m => `${m.manager} ${m.email}`).join(", "), searchValue)}</s2>`} />
              </Typography>
            </Box>
          )}
          {searchResult.submittedBy && (
            <Box sx={headerItemStyle}>
              <Typography variant="subtitle2">
                <Markdown  convertPToDiv children={`<s2>Submitted By: ${highlightText(searchResult.submittedBy, searchValue)}</s2>`} />
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
      <Grid item sx={{display: 'flex', gap: '0.5rem'}}>
        <Typography variant="caption">{fMonthDayYearShort(searchResult.dateAdded)}</Typography>
        {!isDialog && <Iconify icon="mdi:open-in-new" color="primary" onClick={() => setIsDialogOpen(true)}/>}
        {searchResult?.items?.length > 0 && <Iconify
          color="primary"
          sx={{cursor: 'pointer'}}
          icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          onClick={handleClick}
        />}
      </Grid>
    </Grid>
    {open && (
      <Box sx={{ width: '100%' }}>
        <Divider sx={{ width: '100%', my: 1 }} />
        <Scrollbar sx={{ maxHeight: isDialog ? '500px' : '225px', '& .simplebar-scrollbar:before': { opacity: 1 } }}>
          <Box sx={{display: "flex", gap: '0.4rem'}} flexWrap="wrap">
            {searchResult?.items?.map((item, idx) => 
            <Box
              key={idx}
              gap={1}
              sx={{
                backgroundColor: 'rgba(229, 233, 237, 0.42) ',
                borderRadius: '5px',
                padding: '15px',
                border: '1px solid #e5e9ed',
                boxSizing: 'border-box',
                flex: mediumAndUp ? '0 0 calc(50% - 0.2rem)' : '0 0 100%',
                display: "flex",
                alignItems: "center",
            }}>
              <Box>
                <Image
                  width="65px"
                  height="65px"
                  alt="attachment"
                  src={
                    item.pictures?.length
                      ? cloudinaryThumbnail(item.pictures[0])
                      : cloudinaryThumbnail(
                          'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
                        )
                  }
                />
              </Box>
              <Box>
                <Markdown  convertPToDiv children={`<s2>SKU: ${highlightText(item.vendorItemId, searchValue)}</s2>`} />
                <Markdown  convertPToDiv children={`<s2>Vendor: ${highlightText(item.vendor, searchValue)}</s2>`} />
                <Markdown convertPToDiv children={highlightText(item.description, searchValue)} sx={{fontSize: '13px'}}/>
              </Box>
            </Box>)}
          </Box>
        </Scrollbar>
      </Box>
    )}
    </>
}